import { containers, elements, feedback } from '@ecosystem/newron-design-system';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import { ComponentName, initialTrendsKpi } from '@models/Products';
import { RI_PRODUCT_TREND_RETAILER_CARD_TITLE } from '@pages/Utilities';
import ProductsRetailerLineChart from '@data-visualization/products-retailer-line-chart/ProductsRetailerLineChart';
import { CommonCardProps, RetailerCardProps } from '@components/products-line-charts/Types';
import { useGeneratePageElementViewParam } from '@utils/page-analytics/model/PageElementViewedTracking';
import { PageName, PageType, SubSection } from '@utils/pageConfig/Types';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { capitalize, isEmpty } from 'lodash';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { NoResult } from '@pages/NoResult';
import React from 'react';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import ErrorResultMessage from '@pages/ErrorResultMessage';

const TooltipContent: React.FC<{
  excludeTooltipIcon?: boolean;
}> = ({ excludeTooltipIcon }) => (
  <div className="flex">
    {!excludeTooltipIcon && (
      <feedback.Tooltip testId="products-retailer-card-tooltip" placement="top" theme="light">
        <feedback.Tooltip.Content>
          <p>
            Your benchmarked performance for the selected metric. For Revenue/Units, choose between Share (your share of
            each products overall market sales) and Mix (each products share of your overall sales in the category).
          </p>
        </feedback.Tooltip.Content>
        <div className="ml-4 mt-1.5">
          <elements.Icon color="brand">
            <elements.Icon.icons.HelpOutline />
          </elements.Icon>
        </div>
      </feedback.Tooltip>
    )}
  </div>
); // button share here

export interface ProductsRetailerCardProps extends CommonCardProps, RetailerCardProps {
  subSectionForAmplitudeTracking: SubSection;
  isApiError?: boolean;
}

const ProductsRetailerCard: React.FC<ProductsRetailerCardProps> = ({
  trendsKpi,
  selectedPeriodicity,
  selectedLineChartSaleType,
  factRevenue,
  isLoading,
  selectedOption,
  excludeTooltipIcon,
  subSectionForAmplitudeTracking,
  isApiError = false,
}) => {
  const pageElementViewParam = useGeneratePageElementViewParam();
  const pagesConfig = usePageConfig();
  const customPageElementViewParam = () => ({
    ...pageElementViewParam,
    pageType: PageType.RI_PRODUCT,
    pageName: PageName.RI_PRODUCT,
    componentName: ComponentName.PRODUCT_TRENDS_RETAILER_LINE_CHART,
    elementType: ElementType.LINE_CHART,
    elementKpi: capitalize(selectedLineChartSaleType),
    hasData: !isEmpty(trendsKpi?.items),
    access: pagesConfig.access,
    subSection: subSectionForAmplitudeTracking,
  });

  const { ref: ProductRetailerLineChartComponent } = useInViewTracking(customPageElementViewParam);

  const renderContent = () => {
    return renderVisualization({
      isLoading: isLoading,
      fetchedApiData: trendsKpi,
      defaultData: initialTrendsKpi,
      jsxToRender: (
        <div ref={ProductRetailerLineChartComponent}>
          {trendsKpi?.items?.length && (
            <ProductsRetailerLineChart
              data={trendsKpi}
              selectedPeriodicity={selectedPeriodicity}
              currency={factRevenue.currencySymbol}
              selectedLineChartSaleType={selectedLineChartSaleType}
              selectedRetailerOption={selectedOption}
            />
          )}
        </div>
      ),
      noResultJsx: <div ref={ProductRetailerLineChartComponent}>{NoResult()}</div>,
      errorMessageJsx: <div ref={ProductRetailerLineChartComponent}>{ErrorResultMessage()}</div>,
      isError: isApiError,
    });
  };

  const { Card } = containers;
  return (
    <Card>
      <Card.Title title={RI_PRODUCT_TREND_RETAILER_CARD_TITLE}>
        <div style={{ flex: '1' }}>
          <TooltipContent excludeTooltipIcon={excludeTooltipIcon} />
        </div>
      </Card.Title>
      {renderContent()}
    </Card>
  );
};

export default ProductsRetailerCard;
